import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { BUILT, INK, T, createElt } from './elt-model';
import {
  BACKGROUND,
  PLANET_SKY,
  SKY_RADIUS,
  SKY_TARGETS,
  azAltFromDir,
  createConstellations,
  createDeepSky,
  createSky,
  dirFromAzAlt,
  dirFromRaDec,
} from './sky';
import type { LaserWriter } from './laser-writer';
import { createPlanetDiscs } from './planet-discs';
import deepSkyUrl from './sky-deep.bin?url';

// Drives the Santiago hero. The telescope builds itself facing the viewer and its lasers
// write the page's title in the air in front of it (see buildPlan); then the sky is live:
// the telescope follows the pointer, and a click narrows the view into the telescope.
// React owns the DOM overlays; the engine writes their positions and numbers straight
// into the elements it is handed, so nothing re-renders per frame.
//
// Through the telescope ('diving' in, 'eyepiece', 'surfacing' out) it is the same real
// sky, only magnified: the camera rises clear of the dome and narrows to a FIELD_DEG field
// while a round field stop closes in around it. Drag (or the arrow keys) to look around;
// ticks on the stop's rim point to the planets that are up.

export type EngineMode = 'intro' | 'sky' | 'diving' | 'eyepiece' | 'surfacing';

// What sits in the middle of the field, for the read-out.
export type ScopeFocus = { name: string; object: string | null; detail: string | null; fact: string | null };

export type EngineCallbacks = {
  onMode: (mode: EngineMode) => void;
  onFocus?: (focus: ScopeFocus) => void; // what the telescope is centred on changed
  onTitle?: () => void; // the lasers finished writing it: show the real title
  onBuilt?: () => void; // the telescope is whole and starts turning to the title
};

export type EngineDom = {
  // the hovered constellation's name: the engine writes its text, place and opacity
  name: HTMLElement | null;
  // the field stop: a circle the engine sizes, scales and fades
  field?: HTMLElement | null;
  // one marker per planet, placed on the planet or on the rim toward it
  planets?: Map<string, HTMLElement>;
  writer?: LaserWriter | null; // traces the title for the lasers (none: nothing is written)
};

const MIN_ALT = 15;
const SCOPE_MIN_ALT = 4; // the telescope may look lower than its resting aim ever does
const PICK_DEG = 2.2; // how close to one of its stars the pointer wakes a constellation
const TOUCH_PICK_DEG = 3.5;
const NAME_FADE = 0.45; // s: matches the name element's opacity transition
const MAX_ALT = 88;
const FIELD_DEG = 14; // the telescope's widest field, across the field stop
const FIELD_MIN = 0.004; // its narrowest: 14″, Neptune a fifth of the way across
const FIELD_SHARE = 0.42; // the field stop's radius, as a share of the screen's shorter side
const SCOPE_IN = 1.15; // s
const SCOPE_OUT = 0.95; // s
const LIFT = 140; // m the camera rises on the way in, so the dome sinks out of the field

type Key = { t: number; pos: THREE.Vector3; look: THREE.Vector3; fov: number };

// What the intro does between t = 0 and `duration`, all as functions of intro time.
type Plan = {
  duration: number;
  skyAt: number; // when the stars switch on
  keys: Key[];
  clock: (t: number) => number; // the build clock the GPU animations run on
  doors: (t: number, clock: number) => number;
  // the telescope faces the viewer while it builds, settles its aim on the title (`turn`)
  // and writes it in front of itself with its lasers (`write`), in intro seconds
  face: { turn: [number, number]; write: [number, number] };
  // the camera glides through its keys in one ease that ends at this time
  glide: number;
};

const FACE_ALT = 22; // looking out at the viewer while it builds
// share of the camera–telescope distance at which the lasers write: in front of the
// telescope, between it and the viewer
const PEN_SHARE = 0.6;
const FACE_MIN_ALT = 5; // writing toward the viewer may aim lower than it ever does at the sky
const ALT_AXIS = new THREE.Vector3(0, 33, 0);
const UP = new THREE.Vector3(0, 1, 0);

const ease = (x: number) => (x <= 0 ? 0 : x >= 1 ? 1 : x * x * x * (x * (x * 6 - 15) + 10));
const clamp01 = (x: number) => Math.min(1, Math.max(0, x));
const range = (t: number, a: number, b: number) => clamp01((t - a) / (b - a));
const lerp = (a: number, b: number, u: number) => a + (b - a) * u;

function wrapDeg(a: number) {
  return ((((a + 180) % 360) + 360) % 360) - 180;
}

export class EltEngine {
  private renderer: THREE.WebGLRenderer;
  private composer: EffectComposer;
  private bloom: UnrealBloomPass;
  private scene = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera(50, 1, 0.5, SKY_RADIUS * 3);
  private clock = { value: 0 }; // build clock: what the structure's GPU animations read
  private skyClock = { value: 0 }; // starts when the stars do
  private sky: ReturnType<typeof createSky>;
  private deepSky: ReturnType<typeof createDeepSky>;
  private discs: ReturnType<typeof createPlanetDiscs>;
  private constellations: ReturnType<typeof createConstellations>;
  private elt: ReturnType<typeof createElt>;
  private targetDirs = new Map<string, THREE.Vector3>();
  private plan: Plan;
  private rest = { pos: new THREE.Vector3(), look: new THREE.Vector3(), fov: 50 };

  private frame = 0;
  private last = 0;
  private running = false;
  private visible = true;
  private timeScale = 1;
  private time = 0; // real seconds since mount
  private introT = 0; // intro seconds (runs fast when the intro is skipped)

  private mode: EngineMode = 'intro';
  private modeTime = 0;
  private hovered = -1; // the constellation under the pointer
  private named = -1; // the one whose name is on screen (it trails `hovered` by a fade)
  private nameSwap = 0; // when the name element may take the next one

  private pointer = new THREE.Vector2();
  private pointerInside = false;
  private pointerSeen = false;
  private touchInput = false; // the last press was a finger: wider picks, no hover
  private aim = { az: 0, alt: 90 };
  private aimGoal = { az: 0, alt: 90 };
  private domeAz = 0;
  private idlePhase = 0;
  private lasers = 0;
  private lightPath = 0;
  // the telescope: where it looks (eased toward the goal), and the camera it left from
  private view = { az: 0, alt: 30 };
  private viewGoal = { az: 0, alt: 30 };
  private scope = 0; // 0 naked eye → 1 through the telescope
  private field = FIELD_DEG; // the telescope's field, degrees across the stop (eased)
  private fieldGoal = FIELD_DEG;
  private scopeFrom = { pos: new THREE.Vector3(), dir: new THREE.Vector3(), fov: 50 };
  private focusKey = '';
  private raycaster = new THREE.Raycaster();
  private tmp = new THREE.Vector3();
  private tmp2 = new THREE.Vector3();
  private tmpLift = new THREE.Vector3();
  private skySphere = new THREE.Sphere(new THREE.Vector3(), SKY_RADIUS);
  // the writing beams: one from each launch unit, converging on the pen
  private pen = new THREE.Group();
  private penMaterial = new THREE.ShaderMaterial({
    uniforms: { uOpacity: { value: 0 }, uTime: { value: 0 }, uColor: { value: new THREE.Color(INK) } },
    vertexShader: `
      varying float vAlong;
      varying vec3 vNormal;
      varying vec3 vView;
      void main() {
        vAlong = uv.y;
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        vNormal = normalize(normalMatrix * normal);
        vView = normalize(-mv.xyz);
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: `
      uniform float uOpacity;
      uniform float uTime;
      uniform vec3 uColor;
      varying float vAlong;
      varying vec3 vNormal;
      varying vec3 vView;
      void main() {
        float core = pow(abs(dot(normalize(vNormal), normalize(vView))), 1.4);
        float flicker = 0.85 + 0.15 * sin(uTime * 41.0 + vAlong * 90.0);
        float a = min(1.0, core * flicker * uOpacity * (1.1 + 0.6 * smoothstep(0.9, 1.0, vAlong)));
        gl_FragColor = vec4(uColor * a, a);
      }`,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  private penGeometry = new THREE.CylinderGeometry(1, 1, 1, 10, 1, true).translate(0, 0.5, 0);
  private muzzles: THREE.Vector3[] = [];
  private penBeam = 0;
  private titleShown = false;
  private builtShown = false;
  private faceSpring: { az: number; alt: number; vAz: number; vAlt: number } | null = null;

  constructor(
    private container: HTMLElement,
    private dom: EngineDom,
    private callbacks: EngineCallbacks,
    options: { skipIntro: boolean },
  ) {
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 1.75);
    this.renderer = new THREE.WebGLRenderer({ antialias: false, alpha: false, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(pixelRatio);
    // the clear lands in the composer's linear buffer unconverted, so hand it linear values
    this.renderer.setClearColor(new THREE.Color(BACKGROUND).convertSRGBToLinear(), 1);
    this.renderer.domElement.style.display = 'block';
    this.renderer.domElement.style.width = '100%';
    this.renderer.domElement.style.height = '100%';
    container.appendChild(this.renderer.domElement);

    const target = new THREE.WebGLRenderTarget(1, 1, { type: THREE.HalfFloatType, samples: 4 });
    this.composer = new EffectComposer(this.renderer, target);
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    this.bloom = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.28, 0.35, 0.55);
    this.composer.addPass(this.bloom);
    this.composer.addPass(new OutputPass());

    this.sky = createSky(pixelRatio);
    this.scene.add(this.sky.group);
    this.deepSky = createDeepSky(pixelRatio, deepSkyUrl);
    this.scene.add(this.deepSky.group);
    this.discs = createPlanetDiscs(INK);
    this.scene.add(this.discs.group);
    this.elt = createElt(this.clock);
    this.scene.add(this.elt.root);
    this.constellations = createConstellations(INK);
    this.scene.add(this.constellations.group);

    for (let i = 0; i < 4; i++) {
      const beam = new THREE.Mesh(this.penGeometry, this.penMaterial);
      beam.frustumCulled = false;
      this.pen.add(beam);
    }
    this.pen.visible = false;
    this.scene.add(this.pen);

    this.plan = this.buildPlan(1);
    this.resize();
    if (options.skipIntro) this.introT = this.plan.duration + 0.6;
    this.elt.setPointing(0, 90, 0);
  }

  // ── lifecycle ──────────────────────────────────────────────────────────────

  start() {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    const loop = (now: number) => {
      if (!this.running) return;
      this.frame = requestAnimationFrame(loop);
      const dt = Math.min(0.05, (now - this.last) / 1000);
      this.last = now;
      if (!this.visible) return;
      this.tick(dt);
    };
    this.frame = requestAnimationFrame(loop);
  }

  setVisible(visible: boolean) {
    this.visible = visible;
    this.last = performance.now();
  }

  dispose() {
    this.running = false;
    cancelAnimationFrame(this.frame);
    this.sky.dispose();
    this.deepSky.dispose();
    this.discs.dispose();
    this.constellations.dispose();
    this.elt.dispose();
    this.penGeometry.dispose();
    this.penMaterial.dispose();
    this.composer.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }

  resize() {
    const width = Math.max(1, this.container.clientWidth);
    const height = Math.max(1, this.container.clientHeight);
    this.renderer.setSize(width, height, false);
    this.composer.setSize(width, height);
    this.bloom.setSize(width, height);
    this.camera.aspect = width / height;
    this.layout(width / height);
    this.camera.updateProjectionMatrix();
  }

  // ── input ──────────────────────────────────────────────────────────────────

  setPointer(ndcX: number, ndcY: number, inside: boolean) {
    this.pointer.set(ndcX, ndcY);
    this.pointerInside = inside;
    this.touchInput = false;
    if (inside) this.pointerSeen = true;
  }

  // Click / tap inside the hero. Returns whether it was consumed.
  press(ndcX: number, ndcY: number, touch: boolean) {
    if (this.mode === 'intro') {
      this.timeScale = 7;
      return true;
    }
    if (this.mode === 'eyepiece') {
      // a tap through the telescope centres that spot
      this.setPointer(ndcX, ndcY, true);
      this.setViewGoal(this.pointerDir());
      return true;
    }
    if (this.mode !== 'sky') return false;
    this.setPointer(ndcX, ndcY, true);
    const dir = this.pointerDir();
    // look where it was pressed, or at the known object right by it; a finger is less
    // precise, so it snaps from further (the read-out names the constellation either way)
    const hit = this.nearestTarget(dir, touch ? 5 : 3);
    this.look((hit && this.targetDirs.get(hit.id)) || dir);
    if (touch) {
      // a finger leaves no pointer behind: don't let the tap linger as a hover afterwards
      this.pointerInside = false;
      this.touchInput = true;
    }
    return true;
  }

  // Through the telescope: drag by (dx, dy) CSS px, the sky following the hand.
  pan(dx: number, dy: number) {
    if (this.mode !== 'eyepiece' && this.mode !== 'diving') return;
    const perDeg = this.pxPerDeg();
    const cos = Math.max(0.15, Math.cos(THREE.MathUtils.degToRad(this.viewGoal.alt)));
    this.viewGoal.az -= dx / perDeg / cos;
    this.viewGoal.alt = THREE.MathUtils.clamp(this.viewGoal.alt + dy / perDeg, SCOPE_MIN_ALT, MAX_ALT);
    // a drag is direct: no easing lag behind the hand
    this.view.az = this.viewGoal.az;
    this.view.alt = this.viewGoal.alt;
  }

  inScope() {
    return this.mode === 'eyepiece';
  }

  // Through the telescope: narrow (factor < 1) or widen the field.
  zoom(factor: number) {
    if (this.mode !== 'eyepiece') return;
    this.fieldGoal = THREE.MathUtils.clamp(this.fieldGoal * factor, FIELD_MIN, FIELD_DEG);
  }

  // Through the telescope: nudge the aim by degrees (arrow keys).
  nudge(dAz: number, dAlt: number) {
    if (this.mode !== 'eyepiece') return;
    this.viewGoal.az += dAz;
    this.viewGoal.alt = THREE.MathUtils.clamp(this.viewGoal.alt + dAlt, SCOPE_MIN_ALT, MAX_ALT);
  }

  // Through the telescope: slew to the next (or previous) known object.
  step(delta: number) {
    if (this.mode !== 'eyepiece') return;
    const here = dirFromAzAlt(this.viewGoal.az, this.viewGoal.alt);
    let index = 0;
    let best = Number.POSITIVE_INFINITY;
    SKY_TARGETS.forEach((t, i) => {
      const angle = this.targetDirs.get(t.id)?.angleTo(here) ?? Number.POSITIVE_INFINITY;
      if (angle < best) {
        best = angle;
        index = i;
      }
    });
    // off any object, the first step lands on the nearest one
    const centred = best < THREE.MathUtils.degToRad(1);
    const next = SKY_TARGETS[centred ? (index + delta + SKY_TARGETS.length) % SKY_TARGETS.length : index];
    const dir = this.targetDirs.get(next.id);
    if (dir) this.setViewGoal(dir);
    this.fieldGoal = FIELD_DEG;
  }

  // Through the telescope: slew to a planet and close in until it fills about half the field.
  goToPlanet(id: string) {
    const planet = PLANET_SKY.find((p) => p.id === id);
    if (!planet || this.mode !== 'eyepiece') return;
    this.setViewGoal(planet.dir);
    this.fieldGoal = THREE.MathUtils.clamp((this.discs.extent(id) * 2) / 0.5, FIELD_MIN, FIELD_DEG);
  }

  surface() {
    if (this.mode !== 'eyepiece' && this.mode !== 'diving') return;
    this.captureScopeFrom();
    this.mode = 'surfacing';
    this.modeTime = 0;
    this.callbacks.onMode('surfacing');
  }

  skip() {
    if (this.mode === 'intro') this.timeScale = 7;
  }

  // ── internals ──────────────────────────────────────────────────────────────

  // Resting camera and the intro plan for this aspect ratio. Narrow screens step back so
  // the whole dome fits; the sky stays where it really is, they just see less of it.
  private layout(aspect: number) {
    const vfov = aspect < 0.8 ? 58 : 50;
    const hfov = THREE.MathUtils.radToDeg(2 * Math.atan(Math.tan(THREE.MathUtils.degToRad(vfov / 2)) * aspect));
    const domeHalf = 62; // the dome plus some air on both sides
    const fitDistance = domeHalf / Math.tan(THREE.MathUtils.degToRad(hfov / 2));
    const distance = Math.max(aspect < 0.8 ? 270 : 320, fitDistance);
    const pitch = aspect < 0.8 ? 7 : 12;
    this.rest.pos.set(0, aspect < 0.8 ? 30 : 6, distance);
    this.rest.fov = vfov;
    const lookDir = dirFromAzAlt(0, pitch);
    this.rest.look.copy(this.rest.pos).addScaledVector(lookDir, 400);

    for (const t of SKY_TARGETS) this.targetDirs.set(t.id, dirFromRaDec(t.ra, t.dec));

    this.plan = this.buildPlan(Math.max(1, distance / 320), vfov);
  }

  // It builds itself in four seconds facing the viewer, the doors part, and still facing you
  // the lasers write the title in the air between you and the dome — right where the title
  // sits — then it swings round to the sky. `f` scales the camera's path out on narrow
  // screens, which stand further back.
  private buildPlan(f: number, vfov = 50): Plan {
    const L = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);
    const K = (t: number, pos: THREE.Vector3, look: THREE.Vector3, fov = vfov): Key => ({ t, pos, look, fov });
    const restKey = (t: number) => K(t, this.rest.pos.clone(), this.rest.look.clone(), vfov);
    const build = 4;
    return {
      // the sky opens a breath after the last stroke: no dead air between the two
      duration: 10.0,
      skyAt: 4.6,
      clock: (t) => Math.min(BUILT, (t / build) * BUILT),
      doors: (_t, clock) => ease(range(clock, T.doors + 0.2, T.doors + 1.4)),
      face: { turn: [4.2, 6.3], write: [6.5, 9.7] },
      glide: 5.6,
      // one arc, never doubling back: from high over the left shoulder it sweeps round and
      // down to the resting view, so the glide never has to brake at a turnaround
      keys: [0, 1, 2, 3]
        .map((i) => {
          const u = i / 4;
          const theta = THREE.MathUtils.degToRad(-72 * (1 - u));
          const radius = lerp(290 * f, this.rest.pos.z, u);
          const pos = new THREE.Vector3(Math.sin(theta) * radius, lerp(165 * f, this.rest.pos.y, u), Math.cos(theta) * radius);
          return K(u * 5.6, pos, L(0, 18, 0).lerp(this.rest.look, u * u), lerp(vfov + 10, vfov, u));
        })
        .concat(restKey(5.6)),
    };
  }

  // One unbroken move through the keys up to the plan's `glide`: a single ease over the
  // whole of it (still at the start and at the end, never in between), Catmull-Rom through
  // every key for position, look and fov alike, so nothing kinks or pauses at a key.
  private introCamera(t: number) {
    const end = this.plan.glide;
    const keys = this.plan.keys;
    const t0 = keys[0].t;
    const s = t0 + ease(range(t, t0, end)) * (end - t0);
    let i = 0;
    while (i < keys.length - 2 && s > keys[i + 1].t) i++;
    const a = keys[i];
    const b = keys[i + 1];
    const u = range(s, a.t, b.t);
    const k0 = keys[Math.max(0, i - 1)];
    const k3 = keys[Math.min(keys.length - 1, i + 2)];
    return {
      t,
      pos: catmull(k0.pos, a.pos, b.pos, k3.pos, u),
      look: catmull(k0.look, a.look, b.look, k3.look, u),
      fov: catmullScalar(k0.fov, a.fov, b.fov, k3.fov, u),
    };
  }

  // The sky direction under the pointer: where its ray meets the sky sphere, seen from the
  // centre — the frame the stars are placed in — not the ray's own direction, which is off
  // by the camera's distance from the dome (several degrees toward the edges).
  private pointerDir() {
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hit = this.raycaster.ray.intersectSphere(this.skySphere, new THREE.Vector3());
    return hit ? hit.normalize() : this.raycaster.ray.direction.clone();
  }

  // The known object nearest `dir`, within `maxDeg`.
  private nearestTarget(dir: THREE.Vector3, maxDeg: number) {
    let best: string | null = null;
    let bestAngle = THREE.MathUtils.degToRad(maxDeg);
    for (const [id, d] of this.targetDirs) {
      const angle = d.angleTo(dir);
      if (angle < bestAngle) {
        bestAngle = angle;
        best = id;
      }
    }
    return SKY_TARGETS.find((t) => t.id === best) ?? null;
  }

  // Into the telescope, centred on `dir`.
  private look(dir: THREE.Vector3) {
    void this.deepSky.load();
    this.field = FIELD_DEG;
    this.fieldGoal = FIELD_DEG;
    this.captureScopeFrom();
    this.setViewGoal(dir);
    this.view.az = this.viewGoal.az;
    this.view.alt = this.viewGoal.alt;
    this.focusKey = '';
    this.mode = 'diving';
    this.modeTime = 0;
    this.setHovered(-1);
    this.callbacks.onMode('diving');
  }

  private setViewGoal(dir: THREE.Vector3) {
    const { az, alt } = azAltFromDir(dir);
    // take the short way round from wherever the aim is now
    this.viewGoal.az = this.view.az + wrapDeg(az - this.view.az);
    this.viewGoal.alt = THREE.MathUtils.clamp(alt, SCOPE_MIN_ALT, MAX_ALT);
  }

  private captureScopeFrom() {
    this.scopeFrom.pos.copy(this.camera.position);
    this.camera.getWorldDirection(this.scopeFrom.dir);
    this.scopeFrom.fov = this.camera.fov;
  }

  // The camera through the telescope: clear of the dome, its field FIELD_DEG across the stop.
  private scopePos(out: THREE.Vector3) {
    return out.copy(this.rest.pos).add(this.tmpLift.set(0, LIFT, 0));
  }

  private fieldRadius() {
    return FIELD_SHARE * Math.min(this.container.clientWidth, this.container.clientHeight);
  }

  // The camera's focal length in CSS px through the telescope.
  private scopeFocal() {
    return this.fieldRadius() / Math.tan(THREE.MathUtils.degToRad(this.field / 2));
  }

  private pxPerDeg() {
    return this.scopeFocal() * THREE.MathUtils.degToRad(1);
  }

  private scopeFov() {
    return THREE.MathUtils.radToDeg(2 * Math.atan(this.container.clientHeight / 2 / this.scopeFocal()));
  }

  private tick(dt: number) {
    const plan = this.plan;
    this.time += dt;
    this.introT += dt * (this.mode === 'intro' ? this.timeScale : 1);
    const t = this.introT;
    const over = t >= plan.duration;

    this.clock.value = over ? BUILT + (t - plan.duration) : plan.clock(t);
    this.skyClock.value = Math.max(0, t - plan.skyAt);
    this.sky.uniforms.uTime.value = this.skyClock.value;
    this.sky.uniforms.uScope.value = this.scope;
    this.deepSky.uniforms.uScope.value = this.scope;
    // more magnification, fainter stars: the limiting magnitude climbs as the field narrows
    this.deepSky.uniforms.uLimit.value = 6 + this.scope * (2.1 + 0.9 * Math.log10(FIELD_DEG / this.field));

    if (this.mode === 'intro' && over) {
      this.mode = 'sky';
      this.timeScale = 1;
      this.callbacks.onMode('sky');
    }

    if (!this.builtShown && t >= plan.face.turn[0]) {
      this.builtShown = true;
      this.callbacks.onBuilt?.();
    }
    this.elt.setDoors(over ? 1 : plan.doors(t, this.clock.value));
    // it builds itself: no cranes
    this.elt.setCranes(this.clock.value, 0);

    this.updateAim(dt, t);
    this.updateCamera(dt, t);
    this.updateWriting(dt, t);
    this.constellations.update(dt);
    this.updateOverlays();

    this.composer.render();
  }

  // The point under the screen position (x, y), in CSS px of the container, `distance` m
  // along the view ray.
  private rayPoint(x: number, y: number, distance: number, out = new THREE.Vector3()) {
    const ndc = new THREE.Vector2((x / this.container.clientWidth) * 2 - 1, -(y / this.container.clientHeight) * 2 + 1);
    this.raycaster.setFromCamera(ndc, this.camera);
    return this.raycaster.ray.at(distance, out);
  }

  // Where the lasers write: on the view ray, part of the way from the camera to the
  // telescope, so the letters hang in the air between the two (and still land on screen
  // exactly where the title sits).
  private penPoint(x: number, y: number, out = new THREE.Vector3()) {
    return this.rayPoint(x, y, this.camera.position.distanceTo(ALT_AXIS) * PEN_SHARE, out);
  }

  // The intro's pointing: out at the viewer while it builds, then onto the title hanging in
  // front of it (and, once the lasers write, after the pen) — it never turns its back.
  // The aim is a critically damped spring after its goal: it settles onto where the first
  // stroke begins (not the middle of the title, which would jump at the first stroke), then
  // trails the pen, gliding over the hops between letters instead of snapping to them.
  private faceAim(dt: number, t: number) {
    const face = this.plan.face;
    const writer = this.dom.writer?.ready ? this.dom.writer : null;
    const spot = writer ? writer.pen(range(t, face.write[0], face.write[1])) : null;
    let az = 180;
    let alt = FACE_ALT;
    if (spot) {
      const pen = this.penPoint(spot.x, spot.y, this.tmp2);
      ({ az, alt } = azAltFromDir(this.tmp.copy(pen).sub(ALT_AXIS).normalize()));
    }
    const k = ease(range(t, face.turn[0], face.turn[1]));
    const goalAz = 180 + wrapDeg(az - 180) * k;
    const goalAlt = lerp(FACE_ALT, THREE.MathUtils.clamp(alt, FACE_MIN_ALT, MAX_ALT), k);
    if (!this.faceSpring) {
      this.faceSpring = { az: goalAz, alt: goalAlt, vAz: 0, vAlt: 0 };
    }
    // in intro time, so a skipped intro keeps up
    const s = this.faceSpring;
    this.stepSpring(s, goalAz, goalAlt, 6, dt * (this.mode === 'intro' ? this.timeScale : 1));
    this.aim.az = s.az;
    this.aim.alt = s.alt;
    this.domeAz = this.aim.az;
    this.elt.setPointing(this.aim.az, this.aim.alt, this.domeAz);
  }

  // A critically damped spring toward (az, alt) at angular frequency `omega`; small steps
  // keep it stable at any frame rate.
  private stepSpring(s: { az: number; alt: number; vAz: number; vAlt: number }, az: number, alt: number, omega: number, dt: number) {
    let left = dt;
    while (left > 0) {
      const h = Math.min(left, 1 / 240);
      s.vAz += (omega * omega * wrapDeg(az - s.az) - 2 * omega * s.vAz) * h;
      s.vAlt += (omega * omega * (alt - s.alt) - 2 * omega * s.vAlt) * h;
      s.az += s.vAz * h;
      s.alt += s.vAlt * h;
      left -= h;
    }
  }

  // The writing: the trace on the writer's canvas, the four beams converging on the pen,
  // and the hand-off to the real title once it is written.
  private updateWriting(dt: number, t: number) {
    const [w0, w1] = this.plan.face.write;
    const writer = this.dom.writer?.ready ? this.dom.writer : null;
    const u = range(t, w0, w1);
    let target: THREE.Vector3 | null = null;
    if (writer) {
      if (t >= w0) writer.draw(u);
      if (this.mode === 'intro' && t >= w0 && t < w1) {
        const p = writer.pen(u);
        if (p.on) target = this.penPoint(p.x, p.y, this.tmp2);
      }
      // the real logo fades in over the trace (900ms in CSS), then the trace lets go
      writer.setOpacity(1 - ease(range(t, w1 + 0.35, w1 + 1.3)));
    }
    if (!this.titleShown && t >= w1 + 0.05) {
      this.titleShown = true;
      this.callbacks.onTitle?.();
    }
    // the beams fade up over the first strokes, then blank fast while the pen hops between
    // letters
    const goal = target ? ease(range(t, w0, w0 + 0.4)) : 0;
    this.penBeam += (goal - this.penBeam) * (1 - Math.exp(-dt * (target ? 40 : 18)));
    this.penMaterial.uniforms.uOpacity.value = this.penBeam;
    this.penMaterial.uniforms.uTime.value = this.time;
    this.pen.visible = this.penBeam > 0.01 && target !== null;
    if (!this.pen.visible || !target) return;
    this.elt.laserMuzzles(this.muzzles);
    this.pen.children.forEach((beam, i) => {
      const from = this.muzzles[i];
      const dir = this.tmp.copy(target).sub(from);
      const length = dir.length();
      beam.position.copy(from);
      beam.quaternion.setFromUnitVectors(UP, dir.normalize());
      beam.scale.set(1.8, length, 1.8);
    });
  }

  private updateAim(dt: number, t: number) {
    if (this.mode === 'intro') {
      this.faceAim(dt, t);
      this.elt.setLasers(0, this.time);
      this.lightPath += (this.penBeam - this.lightPath) * (1 - Math.exp(-dt * 6));
      this.elt.setLightPath(this.lightPath, this.time);
      return;
    }
    if (this.mode === 'sky') {
      if (this.pointerSeen && this.pointerInside) {
        // the telescope follows the pointer; a figure only stirs when it rests on its stars
        const dir = this.pointerDir();
        const { az, alt } = azAltFromDir(dir);
        this.aimGoal.az = az;
        this.aimGoal.alt = THREE.MathUtils.clamp(alt, MIN_ALT, MAX_ALT);
        this.setHovered(this.constellations.pick(dir, THREE.MathUtils.degToRad(this.touchInput ? TOUCH_PICK_DEG : PICK_DEG), this.hovered));
      } else {
        // attract mode: drift between the objects in view until someone moves the pointer
        this.idlePhase += dt;
        const inView = SKY_TARGETS.filter((t) => {
          const d = this.targetDirs.get(t.id);
          return d && azAltFromDir(d).alt > MIN_ALT && this.project(this.tmp.copy(d).multiplyScalar(SKY_RADIUS)).visible;
        });
        const pick = inView[Math.floor(this.idlePhase / 4.5) % Math.max(1, inView.length)];
        const dir = pick ? this.targetDirs.get(pick.id) : null;
        const { az, alt } = dir ? azAltFromDir(dir) : { az: Math.sin(this.idlePhase * 0.2) * 20, alt: 30 };
        this.aimGoal.az = az;
        this.aimGoal.alt = THREE.MathUtils.clamp(alt, MIN_ALT, MAX_ALT);
        this.setHovered(-1);
      }
    }
    if (this.mode === 'diving' || this.mode === 'eyepiece') {
      // the telescope tracks what we look through it at
      this.aimGoal.az = this.view.az;
      this.aimGoal.alt = THREE.MathUtils.clamp(this.view.alt, MIN_ALT, MAX_ALT);
    }
    // Before it is ready the telescope stays parked at the zenith.
    const ready = t >= this.plan.duration - 0.4;
    const goalAlt = ready ? this.aimGoal.alt : 90;
    const goalAz = ready ? this.aimGoal.az : 0;
    if (this.faceSpring) {
      // The intro hands over from its scripted aim mid-motion: the same spring carries on,
      // momentum and all, only softer — no stall, no lurch at the hand-off. Its first swing
      // round to the sky is a half turn: start it unhurried, firm up after. (A skipped intro,
      // under reduced motion, never wound the spring: the plain follow below takes over.)
      const omega = this.mode === 'diving' ? 6 : lerp(0.9, 3, ease(range(t, this.plan.duration, this.plan.duration + 3)));
      this.stepSpring(this.faceSpring, goalAz, goalAlt, omega, dt);
      this.aim.az = this.faceSpring.az;
      this.aim.alt = this.faceSpring.alt;
    } else {
      const k = 1 - Math.exp(-dt * (this.mode === 'diving' ? 5 : 2.6));
      this.aim.az += wrapDeg(goalAz - this.aim.az) * k;
      this.aim.alt += (goalAlt - this.aim.alt) * k;
    }
    // the dome lags the telescope, as the real one does
    this.domeAz += wrapDeg(this.aim.az - this.domeAz) * (1 - Math.exp(-dt * 1.4));
    this.elt.setPointing(this.aim.az, this.aim.alt, this.domeAz);

    // out through the telescope the beams would only cross the field: they go dark
    const lasersGoal = ready && this.mode === 'sky' ? 0.7 : 0;
    this.lasers += (lasersGoal - this.lasers) * (1 - Math.exp(-dt * 3));
    this.elt.setLasers(this.lasers, this.time);
    const pathGoal = ready ? (this.hovered >= 0 || this.mode !== 'sky' ? 1 : 0.35) : 0;
    this.lightPath += (pathGoal - this.lightPath) * (1 - Math.exp(-dt * 4));
    this.elt.setLightPath(this.lightPath, this.time);
  }

  private setHovered(index: number) {
    if (index === this.hovered) return;
    this.hovered = index;
    this.constellations.setHover(index);
  }

  private updateCamera(dt: number, t: number) {
    const cam = this.camera;

    if (this.mode === 'intro') {
      const k = this.introCamera(t);
      cam.position.copy(k.pos);
      cam.lookAt(k.look);
      this.setFov(k.fov);
      return;
    }

    const restPos = this.tmp.copy(this.rest.pos);
    const restLook = this.tmp2.copy(this.rest.look);
    if (this.mode === 'sky') {
      // a touch of parallax with the pointer
      const px = this.pointerInside ? this.pointer.x : 0;
      const py = this.pointerInside ? this.pointer.y : 0;
      restPos.x += px * 10;
      restPos.y += py * 4;
      restLook.x += px * 25;
      const k = 1 - Math.exp(-dt * 2.5);
      cam.position.lerp(restPos, k);
      const look = cam.position.clone().add(cam.getWorldDirection(new THREE.Vector3()).multiplyScalar(400));
      look.lerp(restLook, k);
      cam.lookAt(look);
      this.setFov(cam.fov + (this.rest.fov - cam.fov) * k);
      this.scope = 0;
      return;
    }

    this.modeTime += dt;
    const m = this.modeTime;
    // the telescope's aim eases after the goal: slow on long slews, quick on small corrections
    const k = 1 - Math.exp(-dt * 4);
    this.view.az += (this.viewGoal.az - this.view.az) * k;
    this.view.alt += (this.viewGoal.alt - this.view.alt) * k;
    if (this.mode === 'eyepiece') {
      // A long slew at high power backs out first, then closes in on arrival: the field
      // never narrows below a couple of times the distance still to go. Even in
      // magnification, like the way in.
      const remaining = THREE.MathUtils.radToDeg(
        dirFromAzAlt(this.view.az, this.view.alt, this.tmp).angleTo(dirFromAzAlt(this.viewGoal.az, this.viewGoal.alt, this.tmp2)),
      );
      const goal = THREE.MathUtils.clamp(Math.max(this.fieldGoal, remaining * 2.5), FIELD_MIN, FIELD_DEG);
      this.field = Math.exp(lerp(Math.log(this.field), Math.log(goal), 1 - Math.exp(-dt * 3.5)));
    }
    // aim at the very point on the sky sphere, so it lands in the middle from up here too
    const target = dirFromAzAlt(this.view.az, this.view.alt, this.tmp2).multiplyScalar(SKY_RADIUS);
    const scopePos = this.scopePos(new THREE.Vector3());
    const scopeDir = target.clone().sub(scopePos).normalize();

    if (this.mode === 'diving' || this.mode === 'eyepiece') {
      // One movement: turn onto the spot (settling first, so the zoom doesn't magnify the
      // turn), rise clear of the dome, and narrow to the telescope's field — evenly in
      // magnification, not in degrees.
      const turn = ease(range(m, 0, SCOPE_IN * 0.75));
      const zoom = ease(range(m, 0.1, SCOPE_IN));
      cam.position.lerpVectors(this.scopeFrom.pos, scopePos, zoom);
      const dir = slerpDir(this.scopeFrom.dir, scopeDir, turn, this.tmp);
      cam.lookAt(this.tmp2.copy(cam.position).addScaledVector(dir, 400));
      this.setFov(this.scopeFrom.fov * (this.scopeFov() / this.scopeFrom.fov) ** zoom);
      this.scope = zoom;
      if (this.mode === 'diving' && m >= SCOPE_IN) {
        this.mode = 'eyepiece';
        this.callbacks.onMode('eyepiece');
      }
      return;
    }

    if (this.mode === 'surfacing') {
      // the same movement backwards, from wherever the telescope was looking
      const back = ease(range(m, 0, SCOPE_OUT));
      const turn = ease(range(m, SCOPE_OUT * 0.2, SCOPE_OUT));
      cam.position.lerpVectors(this.scopeFrom.pos, this.rest.pos, back);
      const restDir = this.tmp2.copy(this.rest.look).sub(this.rest.pos).normalize();
      const dir = slerpDir(this.scopeFrom.dir, restDir, turn, this.tmp);
      cam.lookAt(this.tmp2.copy(cam.position).addScaledVector(dir, 400));
      this.setFov(this.scopeFrom.fov * (this.rest.fov / this.scopeFrom.fov) ** back);
      this.scope = 1 - back;
      if (m >= SCOPE_OUT) {
        this.mode = 'sky';
        this.scope = 0;
        this.callbacks.onMode('sky');
      }
    }
  }

  private setFov(fov: number) {
    if (Math.abs(this.camera.fov - fov) < 1e-4) return;
    this.camera.fov = fov;
    this.camera.updateProjectionMatrix();
  }

  private project(world: THREE.Vector3) {
    const v = world.clone().project(this.camera);
    const behind = v.z > 1;
    return {
      x: (v.x * 0.5 + 0.5) * this.container.clientWidth,
      y: (-v.y * 0.5 + 0.5) * this.container.clientHeight,
      visible: !behind && Math.abs(v.x) < 1.05 && Math.abs(v.y) < 1.05,
    };
  }

  // The hovered figure's name, hung in the middle of it (kept on screen for the big ones).
  // A new name waits for the old one to fade out (the element's own CSS transition).
  private updateName() {
    const el = this.dom.name;
    if (!el) return;
    if (this.named !== this.hovered) {
      if (this.named >= 0) {
        el.style.opacity = '0';
        if (this.time < this.nameSwap) return;
      }
      this.named = this.hovered;
      this.nameSwap = this.time + NAME_FADE;
      if (this.named >= 0) el.textContent = this.constellations.names[this.named];
    }
    if (this.named < 0) return;
    const p = this.project(this.tmp.copy(this.constellations.anchor(this.named)).multiplyScalar(SKY_RADIUS));
    const margin = 48;
    const x = THREE.MathUtils.clamp(p.x, margin, this.container.clientWidth - margin);
    const y = THREE.MathUtils.clamp(p.y, margin, this.container.clientHeight - margin);
    el.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) translate(-50%, -50%)`;
    el.style.opacity = this.mode === 'sky' ? '1' : '0';
    this.nameSwap = this.time + NAME_FADE;
  }

  // The field stop: its radius (as --field-r on the element, which the overlay's ring and
  // blur masks read) starts past the screen's corners at scope 0 and draws in to the field's
  // size as the telescope comes in.
  private updateField() {
    const el = this.dom.field;
    if (!el) return;
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    const radius = this.fieldRadius();
    const open = (Math.hypot(w, h) / 2 / radius) * 1.08;
    const s = this.scope;
    el.style.setProperty('--field-r', `${(radius * (open + (1 - open) * s)).toFixed(1)}px`);
    el.style.opacity = s > 0.001 ? String(Math.min(1, s * 3)) : '0';
  }

  // Through the telescope, every planet above the horizon gets a marker: on the planet when
  // it is in the field, otherwise on the rim, toward it.
  private updatePlanets() {
    // the discs, by how many pixels each globe spans now; their points give way to them
    const focal = this.container.clientHeight / 2 / Math.tan(THREE.MathUtils.degToRad(this.camera.fov / 2));
    const discPx = (id: string) => {
      const planet = PLANET_SKY.find((p) => p.id === id);
      if (!planet || planet.dir.y <= 0) return 0;
      return THREE.MathUtils.degToRad(planet.angularRadius) * focal;
    };
    const largest = this.discs.update(discPx);
    this.sky.uniforms.uPlanetFade.value = 1 - THREE.MathUtils.smoothstep(largest, 2, 5);

    const markers = this.dom.planets;
    if (!markers) return;
    const on = this.mode === 'eyepiece';
    const cx = this.container.clientWidth / 2;
    const cy = this.container.clientHeight / 2;
    const radius = this.fieldRadius();
    for (const planet of PLANET_SKY) {
      const el = markers.get(planet.id);
      if (!el) continue;
      if (planet.dir.y <= 0) {
        el.style.opacity = '0';
        continue;
      }
      const v = this.tmp.copy(planet.dir).multiplyScalar(SKY_RADIUS).project(this.camera);
      const behind = v.z > 1;
      let dx = v.x * cx;
      let dy = -v.y * cy;
      if (behind) {
        dx = -dx;
        dy = -dy;
      }
      const inside = !behind && Math.hypot(dx, dy) < radius * 0.94;
      if (!inside) {
        const angle = Math.atan2(dy, dx);
        dx = Math.cos(angle) * (radius + 12);
        dy = Math.sin(angle) * (radius + 12);
      }
      // up close the marker steps aside: no ring over the globe, the name past its edge
      const px = discPx(planet.id) * (planet.rings ? planet.rings[1] / planet.radiusKm : 1);
      el.dataset.at = inside ? (px > 7 ? 'disc' : 'in') : 'rim';
      el.style.setProperty('--r', `${Math.max(0, px).toFixed(1)}px`);
      // the name goes outward, unless the screen ends first (narrow phones): then inward
      const room = 96;
      const x = cx + dx;
      el.dataset.side = dx < 0 ? (x > room ? 'left' : 'right') : x < this.container.clientWidth - room ? 'right' : 'left';
      el.style.transform = `translate3d(${(cx + dx).toFixed(1)}px, ${(cy + dy).toFixed(1)}px, 0)`;
      el.style.opacity = on ? '1' : '0';
      el.style.pointerEvents = on ? 'auto' : 'none';
    }
  }

  // What the telescope is centred on: a known object, a planet, or just a constellation.
  private updateFocus() {
    if (this.mode !== 'eyepiece' || !this.callbacks.onFocus) return;
    const center = dirFromAzAlt(this.view.az, this.view.alt, this.tmp);
    const target = this.nearestTarget(center, 1.5);
    const planet = PLANET_SKY.find((p) => p.dir.y > 0 && p.dir.angleTo(center) < THREE.MathUtils.degToRad(1));
    const cons = this.constellations.pick(center, THREE.MathUtils.degToRad(9), -1);
    const key = `${cons}|${target?.id}|${planet?.id}`;
    if (key === this.focusKey) return;
    this.focusKey = key;
    const consName = cons >= 0 ? this.constellations.names[cons] : '';
    if (planet) {
      const arcsec = (planet.angularRadius * 7200).toFixed(1).replace('.', ',');
      this.callbacks.onFocus({ name: consName, object: planet.name, detail: `planeta · ${lightTime(planet.distance)} · ${arcsec}″`, fact: planet.fact });
    } else if (target) {
      this.callbacks.onFocus({ name: target.name, object: target.object, detail: `${target.label} · ${target.distance}`, fact: target.fact });
    } else {
      this.callbacks.onFocus({ name: consName, object: null, detail: null, fact: null });
    }
  }

  private updateOverlays() {
    this.updateName();
    this.updateField();
    this.updatePlanets();
    this.updateFocus();
  }
}

// How long the light takes from a planet `au` away, in words.
function lightTime(au: number) {
  const minutes = au * 8.317;
  if (minutes < 90) return `${Math.round(minutes)} minutos luz`;
  return `${(minutes / 60).toFixed(1).replace('.', ',')} horas luz`;
}

// Spherical interpolation between two unit directions.
function slerpDir(a: THREE.Vector3, b: THREE.Vector3, u: number, out: THREE.Vector3) {
  const angle = a.angleTo(b);
  if (angle < 1e-4) return out.copy(b);
  const s = Math.sin(angle);
  return out
    .copy(a)
    .multiplyScalar(Math.sin((1 - u) * angle) / s)
    .addScaledVector(b, Math.sin(u * angle) / s)
    .normalize();
}

function catmullScalar(p0: number, p1: number, p2: number, p3: number, u: number) {
  const u2 = u * u;
  const u3 = u2 * u;
  return 0.5 * (2 * p1 + (-p0 + p2) * u + (2 * p0 - 5 * p1 + 4 * p2 - p3) * u2 + (-p0 + 3 * p1 - 3 * p2 + p3) * u3);
}

function catmull(p0: THREE.Vector3, p1: THREE.Vector3, p2: THREE.Vector3, p3: THREE.Vector3, u: number) {
  const u2 = u * u;
  const u3 = u2 * u;
  const out = new THREE.Vector3();
  for (const axis of ['x', 'y', 'z'] as const) {
    out[axis] =
      0.5 *
      (2 * p1[axis] +
        (-p0[axis] + p2[axis]) * u +
        (2 * p0[axis] - 5 * p1[axis] + 4 * p2[axis] - p3[axis]) * u2 +
        (-p0[axis] + 3 * p1[axis] - 3 * p2[axis] + p3[axis]) * u3);
  }
  return out;
}
